
import React, { useState } from 'react';
import { useGame } from '../../contexts/GameContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DifficultySettings from './sections/DifficultySettings';
import GameSpeedSettings from './sections/GameSpeedSettings';
import ToggleSettings from './sections/ToggleSettings';
import YearDurationSettings from './sections/YearDurationSettings';

interface GameSettingsProps {
  trigger?: React.ReactNode;
}

const GameSettings: React.FC<GameSettingsProps> = ({ 
  trigger = (
    <Button size="sm" variant="outline">
      <Settings className="mr-2 h-4 w-4" />
      Settings
    </Button>
  ) 
}) => {
  const { updateSettings, settings = {
    difficulty: 'Normal',
    gameSpeed: 1,
    tutorialEnabled: true,
    autoSave: true,
    soundEnabled: true,
    yearDuration: 180 // Default to 3 minutes
  } } = useGame();
  
  const [localSettings, setLocalSettings] = useState(settings);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const handleSaveSettings = () => {
    updateSettings(localSettings);
    setOpen(false);
    toast({
      title: "Settings saved",
      description: "Your game settings have been updated.",
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Game Settings</DialogTitle>
          <DialogDescription>
            Customize your Bitcoin Mining Career Simulator experience
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-6 py-4">
          <DifficultySettings 
            localSettings={localSettings}
            setLocalSettings={setLocalSettings}
          />
          
          <GameSpeedSettings 
            localSettings={localSettings}
            setLocalSettings={setLocalSettings}
          />
          
          <YearDurationSettings 
            localSettings={localSettings}
            setLocalSettings={setLocalSettings}
          />
          
          <ToggleSettings 
            localSettings={localSettings}
            setLocalSettings={setLocalSettings}
          />
        </div>
        
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSettings}>Save Settings</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GameSettings;
